import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { ITkPhoneNumTextProps } from "../../../../type/componentsType/TkSuperPowerText";
import TkLabelText from "../../../atoms/TkLabelText";
import "../_style.scss";
import { Box, FormHelperText } from "@mui/material";
import TkPropertyMessageGet from "../../../../utils/TkPropertyMessageGet";
import { TkFormContext } from "../../../../context/tkFormContext";

/**
 * 特殊テキストボックス部品
 * 電話番号を入力するためのグループフォーム
 * @param onChange - 入力値が変更されたときに呼ばれる関数
 * @return TkPhoneNum 特殊テキストボックス部品
 */
export const TkPhoneNum: React.FC<ITkPhoneNumTextProps> = (props) => {
  const {
    size = "small",
    onChange,
    disabled = false,
    wantToSetValue = "",
    onGetInputValue,
    error,
    helperText,
    ...rest
  } = props;
  const phoneList = wantToSetValue?.split("-") || ["", "", ""];
  const { formContext, setFormContext } = useContext(TkFormContext);

  const [firstThreeDigits, setFirstThreeDigits] = useState("");
  const [middleThreeDigits, setMiddleThreeDigits] = useState("");
  const [lastFourDigits, setLastFourDigits] = useState("");
  const [showError, setShowError] = useState("");
  const [pError, setPError] = useState<any>({});
  const minLength = 10;
  const maxLength = 14;

  useEffect(() => {
    setFirstThreeDigits(phoneList[0] || "");
    setMiddleThreeDigits(phoneList[1] || "");
    setLastFourDigits(phoneList[2] || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantToSetValue]);

  /**
   * 電話番号の桁数が変更されたときに呼ばれる関数
   * @param event - 変更イベントオブジェクト
   * @param digits - 変更された桁数
   */
  const handleDigitsChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    digits: "firstThree" | "middleThree" | "lastFour"
  ) => {
    const newValue = event.target.value;

    let newPhoneNum = "";
    let phoneNumForCheck = "";
    let error: any = {};

    switch (digits) {
      case "firstThree":
        setFirstThreeDigits(newValue);
        error = {
          firstThree: !newValue,
          middleThree: !middleThreeDigits,
          lastFour: !lastFourDigits,
        };
        newPhoneNum = `${newValue}-${middleThreeDigits}-${lastFourDigits}`;
        phoneNumForCheck = `${newValue}${middleThreeDigits}${lastFourDigits}`;
        break;
      case "middleThree":
        setMiddleThreeDigits(newValue);
        newPhoneNum = `${firstThreeDigits}-${newValue}-${lastFourDigits}`;
        phoneNumForCheck = `${firstThreeDigits}${newValue}${lastFourDigits}`;
        error = {
          firstThree: !firstThreeDigits,
          middleThree: !newValue,
          lastFour: !lastFourDigits,
        };
        break;
      case "lastFour":
        setLastFourDigits(newValue);
        newPhoneNum = `${firstThreeDigits}-${middleThreeDigits}-${newValue}`;
        phoneNumForCheck = `${firstThreeDigits}${middleThreeDigits}${newValue}`;
        error = {
          firstThree: !firstThreeDigits,
          middleThree: !middleThreeDigits,
          lastFour: !newValue,
        };
        break;
    }
    if (phoneNumForCheck.length === 0) {
      error = {
        firstThree: false,
        middleThree: false,
        lastFour: false,
      };
    }

    let isError = Object.values(error).some((i) => i);
    setPError({
      ...error,
      message: isError ? TkPropertyMessageGet("TKMSG_SCR0043E") : "",
    });

    if (phoneNumForCheck.length === 0) {
      setShowError("");
      setFormContext({
        preventSubmit: { ...formContext?.preventSubmit, phoneNumber: false },
      });
    } else if (phoneNumForCheck.length < minLength) {
      setShowError(TkPropertyMessageGet("TKMSG_SCR0045E", `${minLength}`));
      setFormContext({
        preventSubmit: { ...formContext?.preventSubmit, phoneNumber: true },
      });
    } else if (phoneNumForCheck.length > maxLength) {
      setShowError(TkPropertyMessageGet("TKMSG_SCR0046E", `${maxLength}`));
      setFormContext({
        preventSubmit: { ...formContext?.preventSubmit, phoneNumber: true },
      });
    } else if (isError) {
      setShowError("");
      setFormContext({
        preventSubmit: { ...formContext?.preventSubmit, phoneNumber: true },
      });
    } else {
      setShowError("");
      if (formContext?.preventSubmit?.phoneNumber)
        setFormContext({
          preventSubmit: { ...formContext?.preventSubmit, phoneNumber: false },
        });
    }

    onChange?.({
      target: { value: phoneNumForCheck, valueForShow: newPhoneNum },
    });

    onGetInputValue?.({
      target: { value: phoneNumForCheck, valueForShow: newPhoneNum },
    });
  };

  const className = "phoneInputLabel";
  const inputProps = {
    inputProps: {
      maxLength: 5,
      "data-testid": "phoneNumFirst",
    },
  };
  const inputPropsMiddle = {
    inputProps: { maxLength: 5, "data-testid": "phoneNumMiddle" },
  };
  const inputPropsLast = {
    inputProps: { maxLength: 5, "data-testid": "phoneNumLast" },
  };
  const labelTextProps = {
    className,
    ...rest,
    onlyNumber: true,
  };

  return (
    <Box data-testid="tkPhoneNumInput" className="phoneInputArea">
      <Box className="phoneInput">
        <TkLabelText
          value={firstThreeDigits}
          size={size}
          InputProps={inputProps}
          onChange={(e) => handleDigitsChange(e, "firstThree")}
          {...labelTextProps}
          error={error || !!showError || pError.firstThree}
          disabled={disabled}
          wantToSetValue={firstThreeDigits}
        />

        <TkLabelText
          value={middleThreeDigits}
          InputProps={inputPropsMiddle}
          size={size}
          onChange={(e) => handleDigitsChange(e, "middleThree")}
          {...labelTextProps}
          error={error || !!showError || pError.middleThree}
          disabled={disabled}
          wantToSetValue={middleThreeDigits}
        />

        <TkLabelText
          value={lastFourDigits}
          InputProps={inputPropsLast}
          size={size}
          onChange={(e) => handleDigitsChange(e, "lastFour")}
          {...labelTextProps}
          error={error || !!showError || pError.lastFour}
          disabled={disabled}
          wantToSetValue={lastFourDigits}
        />
      </Box>
      {(error || !!showError || pError.message) && (
        <FormHelperText error>
          {helperText || showError || pError.message}
        </FormHelperText>
      )}
    </Box>
  );
};
export default TkPhoneNum;
